import { default as React, FC, ReactElement, useEffect, useState } from 'react';

import { LeftOutlined } from '@ant-design/icons';
import { Col, notification, Row } from 'antd';
import { FormInstance } from 'antd/lib/form';
import routePaths from 'config/routes';
import { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import CompleteProfilePrompt from 'src/components/account/profile/completeProfilePrompt';
import CustomModal from 'src/components/CustomModal';
import BasicHeader from 'src/components/layout/BasicHeader';
import { handleFilterPharmacies } from 'src/components/pharmacy/Pharmacy.helpers';
import { PharmacyList } from 'src/components/pharmacy/PharmacyList';
import { PharmacyZipCodeForm } from 'src/components/pharmacy/PharmacyZipCodeForm';
import { PharmacyZipCodeInput } from 'src/components/pharmacy/PharmacyZipCodeInput';
import { IRootReducers } from 'src/reducers';
import { IEditPharmacy } from 'src/shared/models/userProfile.model';
import {
  checkProfileCompletion,
  dispatchPageViewEvent,
  isProfileCompleted,
  useWindowSize,
  virtualUrlInit,
} from 'utils';

export interface OwnProps {
  checkout?: boolean;
  account?: boolean;
  form: FormInstance;
  handleClose: () => void;
  idChallenge?: number;
  pharmacySelected: IEditPharmacy;
  setPharmacySelected?: any;
  submitUserPharmacy?: any;
  setZipCode?: React.Dispatch<any>;
  zipCode: string;
  pharmaciesCombo: any;
  setPharmaciesCombo: any;
  pharmaciesComboFiltered: any;
  setPharmaciesComboFiltered: any;
  goBackHeader?: boolean;
  hideList?: boolean;
  children?: ReactElement;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    user: state.auth.user,
  };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const PharmacySelector: FC<Props> = ({
  account,
  challenge,
  checkout,
  goBackHeader,
  handleClose,
  idChallenge,
  pharmaciesCombo,
  pharmaciesComboFiltered,
  pharmacySelected,
  setPharmaciesCombo,
  setPharmaciesComboFiltered,
  setPharmacySelected,
  submitUserPharmacy,
  setZipCode,
  user,
  zipCode,
  hideList = false,
  form,
  children,
}): JSX.Element => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [textFilter, setTextFilter] = useState('');
  const { formatMessage } = useIntl();

  const { isMD } = useWindowSize();
  const isMobile = isMD;
  const isCompleteProfilePage =
    router.route === routePaths.PAGES.MY_ACCOUNT.COMPLETE_PROFILE;

  useEffect(() => {
    if (!textFilter) {
      setPharmaciesComboFiltered(pharmaciesCombo);
    } else {
      const pharmacyFiltered: IEditPharmacy[] = handleFilterPharmacies({
        pharmacyList: pharmaciesCombo,
        textFilter: textFilter,
      });
      setPharmaciesComboFiltered(pharmacyFiltered);
    }
  }, [textFilter]);

  useEffect(() => {
    handlePharmacyAnalytics();
    if (!zipCode || checkout || hideList) {
      return;
    }
    (async () => handleGetPharmaciesComboOptions())();
  }, []);

  useEffect(() => {
    if (pharmacySelected || zipCode) {
      handlePharmacyAnalytics();
    }
  }, [pharmacySelected, zipCode]);

  const handleRestore = () => {
    handleClose();
    setLoading(false);
  };
  const handleOnCancel = () => {
    setPharmacySelected(null);
    handleClose();
    setLoading(false);
  };

  const handleGetPharmaciesComboOptions = async () => {
    try {
      setLoading(true);
      if (checkout && idChallenge) {
        const challengePharmacies = await api.getDataCall({
          dataPath: `${apiPaths.CHALLENGES.CHECKOUT_PHARMACIES}/${idChallenge}?zipcode=${zipCode}`,
          callConfig: null,
        });
        updatePharmaciesCombo(challengePharmacies.data);
      } else if (!checkout) {
        const pharmacies = await api.getPublicApiCombo(
          `${apiPaths.COMBO_IDS.PHARMACIES}&param=${zipCode}`
        );
        updatePharmaciesCombo(pharmacies.data);
      }
    } catch (error) {
      setPharmaciesCombo([]);
      setPharmaciesComboFiltered([]);
      handlePharmacyErrorAnalytics();
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updatePharmaciesCombo = (pharmacies: IEditPharmacy[]) => {
    setPharmaciesCombo(pharmacies);
    setPharmaciesComboFiltered(pharmacies);
    setIsSearching(true);
  };

  const handleSubmit = async () => {
    if (submitUserPharmacy) {
      try {
        setLoading(true);
        await submitUserPharmacy();
      } catch (err) {
        console.group(err);
        notification.warning({
          message: formatMessage({ id: 'form.message.validation-error' }),
        });
      } finally {
        setLoading(false);
      }
    }
    handleRestore();
  };

  const handleChanges = () => (event: any) => {
    setZipCode(event.target.value);
    setTextFilter('');
  };

  const handleOnChangePharmacyList = (event: any) => {
    const item = pharmaciesCombo.find(
      (element) => element.accountName === event.target.value
    );
    setPharmacySelected(item);
    setIsDisabled(false);
  };

  const handleZipCode = async (event: any) => {
    if (event && zipCode) {
      await handleGetPharmaciesComboOptions();
    }
  };

  const handleGoBack = () => {
    handleClose();
  };
  const handlePharmacyAnalytics = () => {
    if (!isFirstRender) return;

    setIsFirstRender(false);

    switch (router.route) {
      case routePaths.PAGES.SIGNUP:
        dispatchPageViewEvent(router, {
          ...virtualUrlInit,
          pageType: 'register',
          detail: 'search-pharmacy',
        });
        break;
      case routePaths.PAGES.EXPERIENCE:
      case routePaths.PAGES.MY_ACCOUNT.MY_PHARMACY:
        dispatchPageViewEvent(router, {
          ...virtualUrlInit,
          pageType: 'pharmacy',
          detail: 'search-pharmacy',
        });
        break;
      case routePaths.PAGES.EXPERIENCE_DETAIL:
        dispatchPageViewEvent(router, {
          ...virtualUrlInit,
          pageType: challenge.challengeCode,
          detail: 'search-pharmacy',
        });
        break;
    }
  };

  const handlePharmacyErrorAnalytics = () => {
    const validRoutes = [
      routePaths.PAGES.SIGNUP,
      routePaths.PAGES.EXPERIENCE,
      routePaths.PAGES.EXPERIENCE_DETAIL,
      routePaths.PAGES.EXPERIENCE_DETAIL,
      routePaths.PAGES.MY_ACCOUNT.MY_PHARMACY,
    ];

    if (validRoutes.some((validRoute) => validRoute === router.route)) {
      dispatchPageViewEvent(router, {
        ...virtualUrlInit,
        pageType: 'error',
        detail: 'search-pharmacy',
      });
    }
  };

  const pageTypeTitle = (
    <>
      <BasicHeader />
      <div className="zipCode-modal__back">
        <Row>
          <button onClick={handleGoBack} className="btn-return">
            <LeftOutlined />
            {formatMessage({ id: 'page.experience.checkout.back' })}
          </button>
        </Row>
      </div>
    </>
  );

  const modalTypeTitle = (
    <Row justify="start">
      <Col xs={20} md={24} className="zipCode-modal__title">
        <FormattedMessage
          id={
            isCompleteProfilePage
              ? 'form.register.section-pharmacy'
              : 'page.center-account.pharmacy-data.edit'
          }
        />
      </Col>
    </Row>
  );

  const renderTitle = () => {
    if (!isMobile) return;
    return goBackHeader ? pageTypeTitle : modalTypeTitle;
  };

  const showCompleteProfilePrompt = Boolean(
    !isProfileCompleted(user?.actionList) && isMD
  );

  return (
    <Row>
      <Col className="pharmacy__selector-content">
        <CustomModal
          centered
          {...{
            customClass: 'zipCode-modal',
            content: (
              <>
                {renderTitle()}
                {showCompleteProfilePrompt && (
                  <CompleteProfilePrompt
                    progress={checkProfileCompletion(user)}
                  />
                )}
                <PharmacyZipCodeForm form={form}>
                  <PharmacyZipCodeInput
                    form={form}
                    handleOnChange={handleChanges}
                    handleOnClick={handleZipCode}
                    zipCode={zipCode}
                  />
                  {children}
                  <PharmacyList
                    form={form}
                    loading={loading}
                    disabled={isDisabled}
                    handleOnChange={handleOnChangePharmacyList}
                    handleOnClick={handleSubmit}
                    onRestore={handleOnCancel}
                    isSearching={zipCode && isSearching}
                    pharmacies={pharmaciesCombo}
                    pharmaciesFiltered={pharmaciesComboFiltered}
                    setFilter={setTextFilter}
                    cancelButton={!!account}
                  />
                </PharmacyZipCodeForm>
              </>
            ),
            showIcon: false,
            visible: true,
            footer: null,
            onCancel: handleOnCancel,
            mask: false,
          }}
        />
      </Col>
    </Row>
  );
};

export default connect(mapStateToProps, null)(PharmacySelector);
